// CosmicMessageTab.tsx
import React, { useState } from "react";
import { Sparkles } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  AstriaMexicoFieldBlock,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import { MXTextInput, MXSubmitButton } from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoCosmicMessageData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Cosmic Message";

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoCosmicMessageData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<Sparkles className="w-5 h-5" />}
          title="Mensaje Cósmico"
          subtitle="Tu día, visto desde el cielo"
          shareText={data.message}
        >
          <p
            style={{
              fontSize: 17,
              lineHeight: 1.7,
              color: colors.bodyColor,
              whiteSpace: "pre-wrap",
              fontFamily: "'Georgia', 'Times New Roman', serif",
            }}
          >
            {data.message}
          </p>
          <AstriaMexicoFieldBlock label="Para Reflexionar" value={data.reflection_prompt} />
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Un Nuevo Mensaje
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<Sparkles className="w-5 h-5" />} title="Mensaje Cósmico">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, onSubmit }) => {
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Mensaje Cósmico
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Un mensaje breve y cálido para hoy, inspirado en lo que se mueve en el cielo — listo
          para compartir.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <MXTextInput value={dob} onChange={setDob} placeholder="Fecha de nacimiento (DD/MM/AAAA)" />
      </div>

      <MXSubmitButton onClick={onSubmit} disabled={!dob.trim()} label="Revelar Mi Mensaje" />
    </>
  );
};

const CosmicMessageTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(`Dame mi Mensaje Cósmico de hoy. Mi fecha de nacimiento: ${dob.trim()}.`);

  const data = message?.astriaMexicoData as AstriaMexicoCosmicMessageData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="cosmic-message">
        {loading && <MXLoadingState label="Mirando el cielo de hoy…" />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="cosmic-message">
      <IntroForm dob={dob} setDob={setDob} onSubmit={handleSubmit} />
    </AstriaMexicoShell>
  );
};

export default CosmicMessageTab;
